import {
  styled,
  Box,
  Switch,
  Button,
  buttonClasses,
  switchClasses,
  FormControlLabel,
  FormControlLabelProps,
  Checkbox,
  CheckboxProps,
} from '@mui/material';
import {
  CheckBoxOutlineBlankOutlined as CheckBlankIcon,
  CheckBoxOutlined as CheckedIcon,
} from '@mui/icons-material';

import AccessFeature from '~components/AccessFeature';
import LoadingFallback from '~components/LoadingFallback';

export const DashboardVariantV2CarrierContentContainer = styled(Box)(
  ({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    flex: 1,
    overflow: 'hidden',
  }),
);

export const DashboardVariantV2CarrierContentTabContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

export const DashboardVariantV2CarrierContentActionsContainer = styled(Box)(
  ({ theme }) => ({
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    alignItems: 'center',

    '&:first-of-type': {
      paddingTop: theme.spacing(2),
    },
  }),
);

export const DashboardVariantV2CarrierContentAccessFeatureButtonSwitch = styled(
  AccessFeature.Button,
)(({ theme }) => ({
  borderColor: 'transparent',
  backgroundColor: 'transparent',

  '&:hover': {
    borderColor: 'transparent',
    backgroundColor: 'transparent',
  },

  [`&.${buttonClasses.disabled}`]: {
    borderColor: 'transparent',
    backgroundColor: 'transparent',

    [`& .${switchClasses.root} .${switchClasses.switchBase}`]: {
      [`& .${switchClasses.thumb}`]: {
        backgroundColor: theme.palette.grey[500],
      },

      [`+ .${switchClasses.track}`]: {
        border: `1px solid ${theme.palette.grey[500]}`,
      },
    },
  },
}));

export const DashboardVariantV2CarrierContentButtonSwitchInput = styled(Switch)(
  ({ theme }) => ({
    width: 28,
    height: 18,
    padding: 0,
    marginLeft: 8,

    [`& .${switchClasses.switchBase}`]: {
      padding: 0,
      margin: 2,
      transitionDuration: '300ms',
      transform: 'translateX(0px)',
      top: 0,

      [`&.${switchClasses.checked}`]: {
        transform: 'translateX(10px)',
        color: theme.palette.primary.main,

        [`& + .${switchClasses.track}`]: {
          backgroundColor: theme.palette.common.white,
          opacity: 1,
          border: `1px solid ${theme.palette.primary.main}`,
        },

        [`& .${switchClasses.thumb}`]: {
          backgroundColor: theme.palette.primary.main,
        },

        [`&.${switchClasses.disabled} .${switchClasses.thumb}`]: {
          opacity: 0.5,
        },
      },

      [`&.${switchClasses.disabled}`]: {
        [`& .${switchClasses.thumb}`]: {
          color:
            theme.palette.mode === 'light'
              ? theme.palette.grey[100]
              : theme.palette.grey[600],
        },

        [`+ .${switchClasses.track}`]: {
          opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
        },
      },
    },

    [`& .${switchClasses.thumb}`]: {
      boxSizing: 'border-box',
      height: 14,
      width: 14,
      backgroundColor: theme.palette.tertiary.main,
      boxShadow: 'none',
    },

    [`& .${switchClasses.track}`]: {
      borderRadius: 26 / 2,
      backgroundColor: theme.palette.common.white,
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
      border: `1px solid ${theme.palette.primary.main}`,
    },
  }),
);

export const DashboardVariantV2CarrierContentFormControlWithBackground = styled(
  (props: FormControlLabelProps) => (
    <FormControlLabel
      {...props}
      componentsProps={{
        typography: {
          variant: 'body2',
        },
      }}
    />
  ),
)(({ theme }) => ({
  border: `1px solid ${theme.palette.action.disabled}`,
  borderRadius: theme.shape.borderRadius,
  marginRight: 0,
  paddingRight: '9px',
  backgroundColor: theme.palette.action.hover,
}));

export const DashboardVariantV2CarrierContentFormControlCheckBox = styled(
  (props: CheckboxProps) => (
    <Checkbox
      {...props}
      color="default"
      icon={<CheckBlankIcon />}
      checkedIcon={<CheckedIcon />}
      size="small"
      disableFocusRipple
      disableRipple
      disableTouchRipple
    />
  ),
)(({ theme }) => ({
  color: theme.palette.common.black,
}));

export const DashboardVariantV2CarrierContentWrapper = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  flex: 1,
  minHeight: 740,
  maxHeight: 740,
}));

export const DashboardVariantV2CarrierContentDataTableContainer = styled(
  'div',
  { shouldForwardProp: (prop) => prop !== 'fullWidth' },
)<{ fullWidth?: boolean }>(({ theme, fullWidth }) => ({
  height: '100%',
  width: 'calc(100vw - 94px)',
  overflowY: 'auto',

  '& .data-table-container': {
    display: 'flex',
    flexDirection: 'column',

    '& ._loading_overlay_wrapper': {
      height: 'auto',
      flex: 1,
    },
  },

  [theme.breakpoints.down(1560)]: {
    ...(!fullWidth && {
      width: 'calc(100vw - 694px)',
    }),
  },

  ...(!fullWidth && {
    width: 'calc(100vw - 994px)',
  }),
}));

export const DashboardVariantV2CarrierContentMapLoading = styled(
  LoadingFallback,
  { shouldForwardProp: (prop) => prop !== 'type' && prop !== 'fullWidth' },
)<{ type: 'relative' | 'absolute'; fullWidth?: boolean }>(
  ({ theme, type, fullWidth }) => ({
    width: 900,
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',

    ...(type === 'absolute' && {
      position: 'absolute',
      zIndex: 4,
      inset: 0,
    }),

    [theme.breakpoints.down(1560)]: {
      width: fullWidth ? 'calc(100vw - 94px)' : 600,
    },

    ...(fullWidth && {
      width: 'calc(100vw - 94px)',
    }),
  }),
);

export const DashboardVariantV2CarrierContentMapContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})<{ fullWidth?: boolean }>(({ theme, fullWidth }) => ({
  width: 900,
  height: '100%',
  position: 'relative',

  '& .mapboxgl-ctrl-top-right': {
    right: 'calc(250px + 16px)',

    '& .mapboxgl-ctrl-group': {
      display: 'flex',

      '& button+button': {
        borderTop: 'none',
        borderLeft: '1px solid #ddd',
      },

      '& button:first-of-type': {
        borderRadius: '4px 0px 0px 4px',
      },

      '& button:last-of-type': {
        borderRadius: '0 4px 4px 0',
      },
    },
  },

  [theme.breakpoints.down(1560)]: {
    width: fullWidth ? 'calc(100vw - 94px)' : 600,
  },

  ...(fullWidth && {
    width: 'calc(100vw - 94px)',
  }),
}));

export const DashboardVariantV2CarrierContentMapToggleFullWidthButton = styled(
  Button,
)({
  display: 'flex',
  position: 'absolute',
  minWidth: 0,
  maxWidth: 32,
  bottom: 2,
  left: 8,
  zIndex: 10,
});

export const DashboardVariantV2CarrierContentMapPopupContent = styled('div')({
  minWidth: 220,
  minHeight: 73,
  display: 'flex',
  alignItems: 'center',
});
